import {DatePipe} from '@angular/common';
import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {
    AcabimAuthService,
    AcabimProject,
    AcabimProjectBuildingStatus,
    AcabimSite,
    ColumnFilter,
    DataTableColumnData,
    DatatableComponent,
    DataTableConfig,
    DataTableDataSource,
    DataTableLazyLoadDataSource,
    DataTableLazyLoadRefresher,
    DataTableObservableCompletionListener,
    DataTableObservableErrorListener, GenericPayloadModel, GenericPayloadType, HelpMessage,
    PaginatedRequest,
    PaginatedResponse,
    ProjectApiService,
    Status,
    TableAction
} from '@cas-hub/acabim-common-client';
import {map, tap} from 'rxjs/operators';
import {WorkOrderService} from '../../../../_services/work-order.service';
import {MessageService, PrimeNGConfig} from 'primeng/api';

export class BuildingStatusFilter {
    public static readonly STATUS_FILTER: ColumnFilter = {
        columnData: {displayName: 'Building Status', objectPropertyName: 'buildingStatus'},
        selectedValue: undefined,
        filterOptions: [
            {label: 'No Building', value: AcabimProjectBuildingStatus[AcabimProjectBuildingStatus.NoBuilding]},
            {
                label: 'Building Available',
                value: AcabimProjectBuildingStatus[AcabimProjectBuildingStatus.BuildingAvailable]
            },
            {
                label: 'Building Pending',
                value: AcabimProjectBuildingStatus[AcabimProjectBuildingStatus.BuildingPending]
            },
        ],
        displayLabel: false
    };

    public static getFilters(): ColumnFilter[] {
        return [BuildingStatusFilter.STATUS_FILTER];
    }
}

@Component({
    selector: 'acabim-admin-projects',
    templateUrl: './projects.component.html',
    styleUrls: ['./projects.component.scss'],
    providers: [MessageService]

})
export class ProjectsComponent implements OnInit {
    @Input() _currentSite?: AcabimSite = undefined;
    @Output() update = new EventEmitter<boolean>();
    isLoading = false;
    closable = true;
    editClosable = true;
    displayAddProjectModal = false;
    archiveError = false;
    errorMessage = '';
    workOrderIds: string[] = [];
    count = '0';
    projectWithWorkOrderIds: string[] = [];
    moveProject = false;
    @ViewChild('projectsTable') public dataTable!: DatatableComponent;

    @ViewChild('archivedProjectsTable') public archivedProjectsTable!: any;

    public projectDataSources: DataTableDataSource<AcabimProject>[] = [];
    archiveCardTitle = 'Archive';
    displayArchiveModal: any;
    archivedProjectData: GenericPayloadModel[] = [];
    dataTypes = GenericPayloadType;

    index = 0;
    superAdmin = false;

    helpMessage: HelpMessage = {
        message: 'Here is a list of projects on the selected site. Click on the eye-icon next to the project name ' +
            'to see its details. Click on the Add Project button below to create another project on this site.'
    };

    constructor(public readonly router: Router,
                private readonly projectAPIService: ProjectApiService,
                protected readonly datePipe: DatePipe,
                private readonly authService: AcabimAuthService,
                private readonly workOrderService: WorkOrderService,
                private messageService: MessageService, private primengConfig: PrimeNGConfig
    ) {
        this.authService.getUser().subscribe(user => {
            this.superAdmin = user ? (user.roles.includes('SuperAdmin')) : false;
        });

    }

    setModal(value: boolean): void {
        this.displayArchiveModal = value;
        if (!value) {
            this.projectDataSources[0].selectedData = [];
            this.archivedProjectData = [];
        }
    }

    setErrorModal(value: boolean): void {
        this.archiveError = value;
    }

    setMoveProjectModal(value: boolean): void {
        this.dataTable.requestImmediateDataRefresh();
        this.moveProject = value;
    }

    setEditModal(value: boolean): void {
        this.projectDataSources[0].showDetails = value;
    }

    close(message: string) {
        if (message === 'cancel') {
            this.projectDataSources[0].showDetails = false;
        } else if (message === 'update') {
            this.projectDataSources[0].showDetails = false;
            this.dataTable?.requestImmediateDataRefresh();
        }

    }

    ngOnInit(): void {
        this.primengConfig.ripple = true;
        if (this._currentSite !== undefined) {
            this.projectDataSources.push(
                // @ts-ignore
                new ProjectDataSource((first, length, sortOrder, sortField, filters) => {
                        const paginatedRequest = PaginatedRequest.FromDatatableRefresher(first, length, sortOrder, sortField);
                        const queryParams = ProjectApiService.createProjectQueryOptionsFromFilters(filters) ?? {};
                        queryParams.filterStatus = Status.active;
                        queryParams.includeBuildingStatus = true;
                        queryParams.includePhase = true;
                        if (this._currentSite) {
                            queryParams.filterSiteId = [this._currentSite.id];
                        }

                        return this.projectAPIService.getAllProjects(paginatedRequest, queryParams)
                            .pipe(map(data => data as PaginatedResponse<AcabimProject>), map(data => ({
                                totalRecords: data.totalRecords,
                                data: data.data
                            })), tap(data => {
                                data.data.forEach(project => {
                                    project.lastModifiedDate = this.getDate(project.lastModifiedDate);
                                });
                            }));
                    },
                    (tableName: string, error: Error) => {
                        if (this.dataTable) {
                            this.dataTable.requestImmediateDataRefresh();
                        }
                        console.log('Error in retrieving data in table ', tableName);
                    },
                    (tableName: string) => {
                        console.log('completion');
                    }, (action: TableAction) => {
                        if (action.id === 0) {
                            this.onProjectArchive();
                        } else {
                            this.moveProject = true;
                        }
                    }
                )
            );
        }
    }

    selectTab($event: { index: number; }) {
        if ($event.index === 0) {
            this.dataTable.requestImmediateDataRefresh();
        } else {
            this.archivedProjectsTable.dataTable.requestImmediateDataRefresh();
        }
        this.index = $event.index;
        console.log(this.index);
    }

    public onAddNewProject(): void {
        if (!this._currentSite) {
            return;
        }
        this.displayAddProjectModal = true;
    }

    getDate(date: string): string {
        const newDate = new Date(date);
        return (this.datePipe.transform(newDate, 'dd MMM yyyy') as string);
    }

    private onProjectArchive() {
        this.archiveError = false;

        if (this.projectDataSources[0].selectedData.length < 1) {
            return;
        }
        this.projectDataSources[0].selectedData.forEach(project => {
            this.archivedProjectData.push({key: project.id, label: project.name, value: project.id, type: this.dataTypes.text});
            this.displayArchiveModal = true;
        });
    }

    public archiveAction(action: boolean) {
        this.workOrderIds = [];
        this.projectWithWorkOrderIds = [];
        if (action) {
            this.isLoading = true;
            this.errorMessage = '';
            this.projectDataSources[0].selectedData.forEach((project, index) => {
                this.projectAPIService.checkProjectWorkOrders(project.id).subscribe((status: any) => {
                    if ((status.data as string[]).length === 0) {
                        this.projectAPIService.archiveProject(project.id).subscribe(data => {
                            if (index === this.projectDataSources[0].selectedData.length - 1) {
                                this.isLoading = false;
                                this.displayArchiveModal = false;
                                this.dataTable?.requestImmediateDataRefresh();
                                this.archivedProjectData = [];
                                this.projectDataSources[0].selectedData = [];
                            }
                        });
                    } else {
                        this.count = status.count.toString();
                        this.workOrderIds = status.data;
                        this.projectWithWorkOrderIds.push(project.id);
                        this.archiveError = true;
                        this.errorMessage = this.errorMessage + project.name + ' ';

                        if (index === this.projectDataSources[0].selectedData.length - 1) {
                            this.isLoading = false;
                            this.displayArchiveModal = false;
                            this.dataTable?.requestImmediateDataRefresh();
                            this.archivedProjectData = [];
                            this.projectDataSources[0].selectedData = [];
                        }
                    }
                });
            });


        } else {
            this.archivedProjectData = [];

            this.displayArchiveModal = false;
        }
    }

    upload(result: boolean) {
        this.closable = result;
    }

    closeModal(close: boolean) {
        this.displayAddProjectModal = close;
        this.dataTable?.requestImmediateDataRefresh();
    }

    public onNewProjectCreated(project: AcabimProject) {
        this.displayAddProjectModal = false;
    }

    setEditClosable(closable: boolean) {
        this.editClosable = closable;
    }

    closeCreatingModal(close: boolean) {
        if (close) {
            this.messageService.clear();
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Project created with existing building'
            });
        }
        this.displayAddProjectModal = false;
        this.dataTable?.requestImmediateDataRefresh();
    }

    closeFromUploading(close: boolean) {
        if (close) {
            this.messageService.clear();
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Upload successful. You will get notification via email'
            });
        }
        this.displayAddProjectModal = false;
        this.dataTable?.requestImmediateDataRefresh();
    }

    forceArchive() {
        this.isLoading = true;
        this.workOrderIds.forEach((id, index) => {
            this.workOrderService.cancelWorkOrders(id).subscribe(() => {
                if (index === this.workOrderIds.length - 1) {
                    this.projectWithWorkOrderIds.forEach(projectid => {
                        this.projectAPIService.archiveProject(projectid).subscribe(() => {
                            this.isLoading = false;
                            this.archiveError = false;
                            this.dataTable?.requestImmediateDataRefresh();
                        });
                    });
                }
            });
        });
    }
}

export class ProjectDataSource extends DataTableLazyLoadDataSource<AcabimProject> {


    readonly columnData: DataTableColumnData[] =
        [
            {displayName: 'Name', objectPropertyName: 'name'},
            {displayName: 'Description', objectPropertyName: 'description'},
            {displayName: 'Project Phase', objectPropertyName: 'currentPhase'},
            {displayName: 'BIM Status', objectPropertyName: 'buildingStatus'},
            {displayName: 'Last Modified', objectPropertyName: 'lastModifiedDate'}
        ];
    readonly config: DataTableConfig = {
        tableHeading: '',
        isSearchEnabled: true,
        searchPlaceHolder: 'Search WorkOrder',
        emptyMessage: '',
        globalFilterFields: [],
        hasCheckbox: true,
        hasDetailsButton: true,
        isLazyLoadingEnabled: true,
        isPaginationEnabled: true,
        hasFilters: true,
        isExportEnabled: false,
        hasActions: true
    };
    readonly columnFilters: ColumnFilter[] = BuildingStatusFilter.getFilters();
    readonly actions: TableAction[] = [{name: 'Archive', id: 0}, {name: 'Move', id: 1}];

    onDataExpanded(value: AcabimProject): void {
    }

    constructor(readonly refresher: DataTableLazyLoadRefresher<AcabimProject>,
                readonly observableErrorListener?: DataTableObservableErrorListener,
                readonly observableCompleteListener?: DataTableObservableCompletionListener,
                readonly actionListener?: (action: TableAction) => void) {
        super(refresher, observableErrorListener, observableCompleteListener);
    }

    onAction(action: TableAction): void {
        if (this.actionListener) {
            this.actionListener(action);
        }
    }

}
